/// <reference lib="es2021" />

import { DependencyList, useContext, useEffect, useMemo, useRef } from 'react';
import type { AnyAction, Middleware } from 'redux';
import { ReactReduxContext } from 'react-redux';

type Subscription = (newState: any, action: AnyAction) => void;

const subscriptions = new Set() as Set<Subscription>;

type Selected<S extends Record<string, (state: any) => any>> = { [K in keyof S]: ReturnType<S[K]> };

type CurrentAndPrevious<S extends Record<string, any>> = { [K in keyof S]: { current: S[K], previous: S[K] } };

export const useReduxMonitor = <S extends Record<string, (state: any) => any>>(
  // selectors will be cached and re-computed whenever dependencies change
  selectors: S, // TODO: make this callback instead
  onChange: (values: CurrentAndPrevious<Selected<S>>, action: AnyAction) => void,
  deps: DependencyList,
  actions = [] as Array<string>, // optionally provide a list of action types that will always trigger the callback
): Selected<S> => {
  // return all selector values to be used outside
  const selectedValues = useRef<Selected<S> | undefined>();
  const proxy = useRef() as typeof selectedValues;

  const { store } = useContext(ReactReduxContext);
  useMemo(() => {
    // we can use useMemo to re-run the following block when dependencies are updated
    const state = store.getState();
    // const state = store?.deref()?.getState();
    selectedValues.current = Object.keys(selectors).reduce((acc, key) => {
      acc[key] = selectors[key](state);
      return acc;
    }, {}) as Selected<S>;
    proxy.current = new Proxy(selectedValues, {
      get: (target, p) => target.current[p as any],
    }) as Selected<S>;
  }, deps);

  useEffect(() => {
    const subscription: Subscription = (newState, action) => {
      let changed = false;
      const newValues = Object.keys(selectors).reduce((acc, key) => Object.assign(acc, { [key]: selectors[key](newState) }), {} as Selected<S>);
      const oldAndNewValues = Object.keys(selectors).reduce((acc, key) => {
        acc[key] = {
          previous: selectedValues.current[key],
          current: newValues[key],
        };
        changed ||= acc[key].current !== acc[key].previous;
        return acc;
      }, {} as CurrentAndPrevious<Record<string, any>>) as CurrentAndPrevious<Selected<S>>;
      selectedValues.current = newValues;
      if (changed || actions.find((type) => type === action.type)) {
        onChange(oldAndNewValues, action);
      }
    };
    subscriptions.add(subscription);
    return () => { subscriptions.delete(subscription); };
  }, deps);

  // store all selected values
  // on redux change, get all changes, action, and call onChange
  // return selected values
  return proxy.current;
};

export const middleware: Middleware = (api) => (next) => (action: AnyAction) => {
  const result = next(action);
  if (subscriptions.size > 0) {
    const newState = api.getState();
    subscriptions.forEach((fn) => {
      fn(newState, action);
    });
  }
  return result;
};
